import {
  MAX_INVOICE_QUANTITY_WHOLE_DIGITS,
  MAX_INVOICE_RATE_WHOLE_DIGITS,
  type Currency,
  type InvoiceDetail,
} from './invoice.ts';

export interface DecimalParseOptions {
  maxScale: number;
  maxWholeDigits?: number;
  positive?: boolean;
}

export interface ParsedDecimal {
  units: bigint;
  scale: number;
}

export interface LineAmountInput {
  quantity: string;
  rate: string;
}

export function parseCanonicalDecimal(
  value: string,
  { maxScale, maxWholeDigits, positive = false }: DecimalParseOptions,
): ParsedDecimal {
  const wholeDigitPattern = maxWholeDigits === undefined ? '\\d*' : `\\d{0,${maxWholeDigits - 1}}`;
  const pattern = new RegExp(`^(?:0|[1-9]${wholeDigitPattern})(?:\\.\\d{1,${maxScale}})?$`);
  if (!pattern.test(value)) {
    throw new Error('Invalid decimal.');
  }

  const [whole, fraction = ''] = value.split('.');
  const paddedFraction = fraction.padEnd(maxScale, '0');
  const units = BigInt(whole!) * 10n ** BigInt(maxScale) + BigInt(paddedFraction || '0');

  if (positive && units <= 0n) {
    throw new Error('Decimal must be positive.');
  }

  return { units, scale: maxScale };
}

export function computeLineAmountCents(input: LineAmountInput): number {
  const quantity = parseCanonicalDecimal(input.quantity, {
    maxScale: 3,
    maxWholeDigits: MAX_INVOICE_QUANTITY_WHOLE_DIGITS,
    positive: true,
  });
  const rate = parseCanonicalDecimal(input.rate, {
    maxScale: 2,
    maxWholeDigits: MAX_INVOICE_RATE_WHOLE_DIGITS,
    positive: true,
  });
  const raw = quantity.units * rate.units;
  const divisor = 10n ** BigInt(quantity.scale);
  const whole = raw / divisor;
  const remainder = raw % divisor;
  const rounded = remainder * 2n >= divisor ? whole + 1n : whole;
  if (rounded > BigInt(Number.MAX_SAFE_INTEGER)) {
    throw new Error('Line amount exceeds safe integer range.');
  }
  return Number(rounded);
}

export function computeInvoiceTotalCents(items: LineAmountInput[]): number {
  return items.reduce((total, item) => {
    const nextTotal = total + computeLineAmountCents(item);
    if (nextTotal > Number.MAX_SAFE_INTEGER) {
      throw new Error('Invoice total exceeds safe integer range.');
    }
    return nextTotal;
  }, 0);
}

export function computeInvoiceTaxCents(subtotalCents: number, taxRate: string): number {
  if (!Number.isInteger(subtotalCents) || subtotalCents < 0) {
    throw new Error('Subtotal cents must be a non-negative integer.');
  }
  const rate = parseCanonicalDecimal(taxRate, { maxScale: 2 });
  if (rate.units === 0n) return 0;
  // rate.units is the rate in 0.01% steps (e.g. "20" -> 2000, "7.5" -> 750).
  // taxCents = subtotalCents * rate.units / (100 * 10^scale) = subtotalCents * rate.units / 10000.
  const denominator = 100n * 10n ** BigInt(rate.scale);
  const raw = BigInt(subtotalCents) * rate.units;
  const whole = raw / denominator;
  const remainder = raw % denominator;
  const rounded = remainder * 2n >= denominator ? whole + 1n : whole;
  if (rounded > BigInt(Number.MAX_SAFE_INTEGER)) {
    throw new Error('Invoice tax exceeds safe integer range.');
  }
  return Number(rounded);
}

export function formatCurrencyCents(cents: number, currency: Currency): string {
  if (!Number.isInteger(cents) || cents < 0) {
    throw new Error('Cents must be a non-negative integer.');
  }

  const whole = Math.floor(cents / 100);
  const fraction = String(cents % 100).padStart(2, '0');
  const grouped = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const amount = `${grouped},${fraction}`;

  if (currency === 'USD') return `$${amount}`;
  if (currency === 'EUR') return `${amount} €`;
  return `${amount} ₽`;
}

export function isIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year!, month! - 1, day));
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month! - 1 && date.getUTCDate() === day
  );
}

export function todayLocalIsoDate(date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function addDaysToIsoDate(base: string, days: number): string {
  const [year, month, day] = base.split('-').map(Number);
  const shifted = new Date(Date.UTC(year!, month! - 1, day! + days));
  const shiftedYear = shifted.getUTCFullYear();
  const shiftedMonth = String(shifted.getUTCMonth() + 1).padStart(2, '0');
  const shiftedDay = String(shifted.getUTCDate()).padStart(2, '0');
  return `${shiftedYear}-${shiftedMonth}-${shiftedDay}`;
}

const PDF_FILENAME_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

export function buildPdfFilename(
  invoice: Pick<InvoiceDetail, 'number' | 'date'>,
  senderProfile: Pick<InvoiceDetail['senderProfile'], 'name'>,
): string {
  if (!Number.isInteger(invoice.number) || invoice.number < 1) {
    throw new Error('Invalid invoice number.');
  }
  if (!isIsoDate(invoice.date)) {
    throw new Error('Invalid invoice date.');
  }

  const [, rawMonth] = invoice.date.split('-').map(Number);
  const month = PDF_FILENAME_MONTHS[rawMonth! - 1];
  const year = invoice.date.slice(2, 4);
  const senderName = senderProfile.name.replace(/\s/g, '_').replace(/[^A-Za-z0-9_]/g, '');
  const safeSenderName = senderName === '' ? 'Sender' : senderName;

  return `${safeSenderName}_Invoice${invoice.number}_${month}${year}.pdf`;
}
